import { Game } from './Game';
import { Text } from './Text';
import { Input } from './Input';
import { ReplaySampler, renderReplaySnapshot, replayTakeoverIssue } from './ReplayEngine';
import { TestUI } from './TestUI';
import { downloadReplay } from './ReplayDownload.mjs';
import { GAME_WIDTH, GAME_HEIGHT, CHAR_HEIGHT } from './Constants';
import { ReplayRecorder, MAX_REPLAY_FRAMES, REPLAY_WIDTH, REPLAY_HEIGHT, replayFrameAt, replaySnapshotAt, replayTimelineEvents, replayDescription, decodeReplay, validateReplay } from './Replay.mjs';

const $ = id => document.getElementById(id);
const clock = ms => `${Math.floor(ms / 60000)}:${String(Math.floor(ms / 1000) % 60).padStart(2, '0')}`;
const MAX_LIBRARY_FRAMES = 24000, MAX_RECORDINGS = 8;
const RECORDING_MODES = ['off', 'next', 'all'];
const RECORDING_LABELS = ['Off', 'Next Game', 'All Games'];

export const ReplayUI = {
    recordings: [], selected: '', position: 0, recordingMode: 'off', recordingSession: null, playing: false,
    init(workshop) {
        this.workshop = workshop; workshop.replays = this;
        $('replays-tab').onclick = () => workshop.show('replays');
        document.querySelectorAll('[data-record-mode]').forEach(slider => {
            slider.oninput = () => this.setRecordingMode(RECORDING_MODES[Number(slider.value)]);
        });
        document.querySelectorAll('[data-view-recordings]').forEach(button => button.onclick = () => workshop.show('replays'));
        $('replay-select').onchange = () => { this.pause(); this.selected = $('replay-select').value; this.position = 0; this.frameOverride = undefined; this.render(); };
        $('replay-play').onclick = () => this.togglePlayback();
        $('replay-return').onclick = () => this.returnToWorkshop();
        $('replay-previous').onclick = () => this.step(-1);
        $('replay-next').onclick = () => this.step(1);
        $('replay-takeover').onclick = () => this.takeOver();
        $('replay-index-form').onsubmit = event => {
            event.preventDefault();
            const data = this.current()?.data, index = Number($('replay-index').value) - 1;
            if (data && Number.isInteger(index) && index >= 0 && index < data.frames.length) {
                this.selectFrame(index);
            }
        };
        $('replay-seek').onpointerdown = () => this.pause();
        $('replay-seek').oninput = () => this.selectFrame(Number($('replay-seek').value) - 1);
        $('replay-open').onclick = () => $('replay-file').click();
        $('replay-file').onchange = event => this.openFile(event.target.files[0]);
        $('replay-download').onclick = () => this.download();
        $('replay-remove').onclick = () => {
            this.pause(); this.recordings = this.recordings.filter(item => item.id !== this.selected);
            this.selected = this.recordings.at(-1)?.id || ''; this.position = 0; this.frameOverride = undefined; this.render();
            this.message('Recording removed from this visit. Downloaded files are unchanged.');
        };
        Game.onScreenDraw = () => this.capture();
        Input.onConsumed = action => {
            if (this.recorder && Game.session === this.sourceSession && this.pendingActions.length < 32) this.pendingActions.push(action);
        };
        Game.stopRecording = reason => this.endRecordingSession(reason);
        Game.onSessionStart = () => this.beginRecordingSession();
        window.addEventListener('ladder-ready', () => { this.updateRecordingButtons(); this.draw(); });
        window.addEventListener('beforeunload', event => {
            if (this.recorder || this.recordings.some(item => !item.saved)) { event.preventDefault(); event.returnValue = ''; }
        });
        document.addEventListener('visibilitychange', () => { if (document.hidden) this.pause(); });
        new ResizeObserver(() => { this.renderMarkers(); this.draw(); }).observe($('replay-canvas'));
        this.updateRecordingButtons(); this.render();
    },
    current() { return this.recordings.find(item => item.id === this.selected); },
    usedFrames() { return this.recordings.reduce((sum, item) => sum + item.data.frames.length, 0); },
    message(text) { $('replay-status').textContent = text; },
    updateRecordingButtons(message) {
        if (message) this.recordingMessage = message;
        const index = RECORDING_MODES.indexOf(this.recordingMode);
        const thisGame = this.recordingMode === 'next' && !!this.recordingSession;
        document.querySelectorAll('[data-record-mode]').forEach(slider => {
            slider.value = index;
            slider.setAttribute('aria-valuetext', thisGame ? 'This Game' : RECORDING_LABELS[index]);
            slider.disabled = !Game.ready;
            const control = slider.closest('.recording-mode');
            control.dataset.mode = this.recordingMode;
            control.dataset.recording = String(thisGame && !!this.recorder);
            control.querySelector('[data-record-next-label]').textContent = thisGame ? 'This Game' : 'Next Game';
        });
        const status = this.recorder
            ? 'Recording ● Player, boulders and board changes are being captured.'
            : this.recordingSession
                ? (this.recordingMessage || 'Recording has stopped for this game. The selected mode applies to future games.')
                : (this.recordingMode === 'next' ? 'The next game will be recorded from the start, then recording switches Off.'
                    : this.recordingMode === 'all' ? 'Every new game will be recorded. Choose Off to stop.'
                    : 'Recording is Off.');
        document.querySelectorAll('[data-record-status]').forEach(element => {
            element.textContent = status + (!this.recorder && !this.recordingSession && this.recordingMessage ? ' ' + this.recordingMessage : '');
        });
    },
    setRecordingMode(mode) {
        if (!RECORDING_MODES.includes(mode) || mode === this.recordingMode) return;
        this.recordingMode = mode;
        this.recordingMessage = '';
        if (mode === 'off') {
            this.recordingSession = null;
            this.stopRecording('Stopped by player');
        }
        this.updateRecordingButtons();
    },
    beginRecordingSession() {
        if (this.recordingMode === 'off' || !Game.session || this.recordingSession === Game.session) return;
        this.recordingSession = Game.session;
        this.recordingMessage = '';
        this.startRecording();
    },
    endRecordingSession(reason) {
        // Only a game that claimed this setting can consume Next Game.
        // A recording limit may stop capture before the game itself ends.
        if (this.recordingSession) {
            this.recordingSession = null;
            if (this.recordingMode === 'next') this.recordingMode = 'off';
        }
        this.stopRecording(reason);
        this.updateRecordingButtons();
    },
    startRecording() {
        const session = Game.session;
        if (!session || this.recordings.length >= MAX_RECORDINGS || this.usedFrames() >= MAX_LIBRARY_FRAMES) {
            this.updateRecordingButtons('Download and remove an older recording in Recordings to make room.'); return;
        }
        this.sampler = new ReplaySampler(); this.pendingActions = [];
        const board = session.levels[session.levelNumber % session.levels.length];
        const title = session.replayPractice ? `Replay practice · ${board.name}`
            : session.testing ? `Board test · ${board.name}`
                : session.originalCampaign ? 'Original Series' : this.workshop?.recordingSeriesName || 'Custom Series';
        this.recorder = new ReplayRecorder(title,
            { maxFrames: Math.min(MAX_REPLAY_FRAMES, MAX_LIBRARY_FRAMES - this.usedFrames()) });
        this.sourceSession = session;
        this.updateRecordingButtons();
        this.capture();
    },
    capture() {
        if (!this.recorder) {
            if (this.recordingSession && (Game.session !== this.recordingSession || Game.session.testResult)) this.endRecordingSession('Board test ended');
            return;
        }
        if (Game.session !== this.sourceSession) { this.endRecordingSession('Game changed'); return; }
        if (!Game.session?.field) return;
        try {
            const snapshot = this.sampler.capture(Game.session), actions = this.pendingActions.splice(0);
            if (!this.recorder.capture(snapshot, performance.now(), actions)) this.stopRecording(this.recorder.data.reason);
            else if (Game.session.testResult) this.endRecordingSession(Game.session.testResult === 'completed' ? 'Board completed' : 'Board test ended');
        } catch { this.stopRecording('Recording stopped after a capture error'); }
    },
    stopRecording(reason = 'Recording stopped') {
        // Finish captured data without changing the policy for future games.
        if (!this.recorder) return;
        if (Game.session === this.sourceSession && Game.session?.field && !this.recorder.stopped) {
            try { this.recorder.capture(this.sampler.capture(Game.session), performance.now(), this.pendingActions.splice(0)); }
            catch { reason = 'Recording stopped after a capture error'; }
        }
        const data = this.recorder.finish(performance.now(), reason);
        this.recorder = null; this.sourceSession = null;
        if (data) {
            const item = { id: crypto.randomUUID(), data, saved: false };
            this.recordings.push(item); this.selected = item.id; this.position = 0; this.frameOverride = undefined;
            this.updateRecordingButtons(`${data.reason}. View it in Recordings and download to keep it.`);
            this.render();
        } else this.updateRecordingButtons('No gameplay was captured. Start a game and try again.');
    },
    onViewChange(visible) {
        this.pause();
        if (visible) this.render();
    },
    render() {
        $('replay-select').replaceChildren(...this.recordings.map(item => new Option(`${replayDescription(item.data)} · ${clock(item.data.duration)}`, item.id)));
        $('replay-select').value = this.selected;
        const item = this.current();
        $('replay-empty').hidden = !!item;
        $('replay-player').hidden = !item;
        $('replay-return').hidden = !item?.workshopReturn;
        $('replay-select').disabled = !item;
        for (const id of ['replay-download', 'replay-remove']) $(id).disabled = !item || !!this.busy;
        $('replay-open').disabled = !!this.busy;
        if (item) {
            $('replay-title').textContent = replayDescription(item.data);
            $('replay-info').textContent = `${new Date(item.data.createdAt).toLocaleString()} · ${item.data.frames.length.toLocaleString()} recorded states · ${item.data.reason}`;
            $('replay-seek').max = item.data.frames.length;
            $('replay-seek').disabled = item.data.frames.length === 1;
            $('replay-index').max = item.data.frames.length;
        }
        this.renderMarkers(); this.draw();
    },
    renderMarkers() {
        const rail = $('replay-events'), data = this.current()?.data;
        rail.replaceChildren();
        const events = data ? replayTimelineEvents(data) : [];
        rail.hidden = !events.length;
        $('replay-events-legend').hidden = !events.length;
        const lanes = [], width = Math.max(1, rail.clientWidth);
        let deaths = 0;
        for (const event of events) {
            const fraction = data.frames.length > 1 ? event.index / (data.frames.length - 1) : 0;
            const x = fraction * width;
            let lane = lanes.findIndex(end => x - end >= 44);
            if (lane < 0) lane = lanes.length;
            lanes[lane] = x;
            const button = document.createElement('button');
            button.type = 'button'; button.className = 'replay-event replay-event-' + event.type;
            button.textContent = event.type === 'level' ? String(event.level) : event.type === 'success' ? '$' : '☠';
            const label = (event.type === 'level' ? 'Level ' + event.level + ' begins · ' + data.frames[event.index].state.name : event.type === 'success' ? 'Board completed' : 'Death ' + (++deaths)) +
                ' · frame ' + (event.index + 1) + ' · ' + clock(event.at) + '.' + Math.floor(event.at % 1000 / 100);
            button.title = label; button.setAttribute('aria-label', label);
            button.dataset.frame = event.index;
            button.style.left = (fraction * 100) + '%'; button.style.top = (lane * 44) + 'px';
            button.onclick = () => this.selectFrame(event.index);
            rail.append(button);
        }
        rail.style.height = (lanes.length * 44) + 'px';
    },
    draw() {
        const data = this.current()?.data, canvas = $('replay-canvas');
        if (!data || !Text.glow || $('replays-view').hidden || !canvas.clientWidth) return;
        this.position = Math.max(0, Math.min(this.position, data.duration));
        const index = this.frameOverride ?? replayFrameAt(data, this.position);
        const snapshot = replaySnapshotAt(data, index);
        const text = renderReplaySnapshot(snapshot);
        const issue = replayTakeoverIssue(snapshot);
        $('replay-takeover').disabled = !!issue;
        $('replay-takeover-help').textContent = issue || (snapshot.state.resume
            ? 'Take control of this moment in a practice attempt. Future boulders and bounces may differ.'
            : 'Older recording: practice uses base difficulty and a fresh movement timer.');
        if (document.activeElement !== $('replay-index')) $('replay-index').value = index + 1;
        const dpi = window.devicePixelRatio || 1;
        canvas.width = Math.round(canvas.clientWidth * dpi); canvas.height = Math.round(canvas.clientHeight * dpi);
        const context = canvas.getContext('2d'), scale = Math.min(canvas.width / GAME_WIDTH, canvas.height / GAME_HEIGHT);
        context.fillStyle = '#181818'; context.fillRect(0, 0, canvas.width, canvas.height);
        context.imageSmoothingEnabled = false;
        context.setTransform(scale, 0, 0, scale, (canvas.width - GAME_WIDTH * scale) / 2, (canvas.height - GAME_HEIGHT * scale) / 2);
        for (let y = 0; y < REPLAY_HEIGHT; y++) Text.drawText(context, text.slice(y * REPLAY_WIDTH, (y + 1) * REPLAY_WIDTH), 0, y * CHAR_HEIGHT * Text.glow.scale, Text.glow);
        $('replay-seek').value = index + 1;
        $('replay-events').querySelectorAll('button').forEach(button => {
            if (Number(button.dataset.frame) === index) button.setAttribute('aria-current', 'true');
            else button.removeAttribute('aria-current');
        });
        $('replay-seek').style.setProperty('--position', `${data.frames.length > 1 ? index / (data.frames.length - 1) * 100 : 0}%`);
        const time = `${clock(this.position)}.${Math.floor(this.position % 1000 / 100)}`;
        $('replay-time').textContent = `Frame ${index + 1} of ${data.frames.length} · ${time} / ${clock(data.duration)}`;
        $('replay-seek').setAttribute('aria-valuetext', `Frame ${index + 1} of ${data.frames.length}, at ${time}`);
        $('replay-previous').disabled = index === 0;
        $('replay-next').disabled = index === data.frames.length - 1;
    },
    pause() {
        this.playing = false; cancelAnimationFrame(this.animation);
        $('replay-play-label').textContent = 'Play recording';
        $('replay-play-icon').textContent = '▶';
    },
    togglePlayback() {
        if (this.playing) { this.pause(); return; }
        const data = this.current()?.data;
        if (!data || !Game.ready) return;
        if (this.position >= data.duration) this.position = 0;
        this.frameOverride = undefined;
        this.playing = true; this.lastTime = performance.now();
        $('replay-play-label').textContent = 'Pause recording';
        $('replay-play-icon').textContent = '❚❚';
        const tick = now => {
            if (!this.playing) return;
            this.position = Math.min(data.duration, this.position + (now - this.lastTime) * Number($('replay-speed').value));
            this.lastTime = now; this.draw();
            if (this.position >= data.duration) this.pause();
            else this.animation = requestAnimationFrame(tick);
        };
        this.animation = requestAnimationFrame(tick);
    },
    selectFrame(index) {
        const data = this.current()?.data;
        if (!data || !Number.isInteger(index) || index < 0 || index >= data.frames.length) return;
        this.pause(); this.position = data.frames[index].at; this.frameOverride = index; this.draw();
    },
    step(direction) {
        this.pause(); const data = this.current()?.data;
        if (!data) return;
        const index = Math.max(0, Math.min(data.frames.length - 1, (this.frameOverride ?? replayFrameAt(data, this.position)) + direction));
        this.selectFrame(index);
    },
    takeOver() {
        const item = this.current();
        if (!item || !Game.ready) return;
        this.pause();
        const index = this.frameOverride ?? replayFrameAt(item.data, this.position);
        const snapshot = replaySnapshotAt(item.data, index), issue = replayTakeoverIssue(snapshot);
        if (issue) { this.message(issue); return; }
        const bookmark = { selected: item.id, position: this.position, frameOverride: index };
        TestUI.startReplay(snapshot, {
            context: `${item.data.title} · frame ${index + 1} · ${clock(item.data.frames[index].at)}`,
            elapsedMs: Math.max(0, this.position - item.data.frames[index].at),
            onLeave: () => { Object.assign(this, bookmark); }
        });
    },
    addRecording(data, saved = false) {
        data = validateReplay(data);
        if (this.recordings.length + (this.recorder ? 1 : 0) >= MAX_RECORDINGS || this.usedFrames() + data.frames.length + (this.recorder?.maxFrames || 0) > MAX_LIBRARY_FRAMES) {
            throw new Error('Download and remove an older recording to make room.');
        }
        const item = { id: crypto.randomUUID(), data, saved };
        this.recordings.push(item);
        return item;
    },
    watchRecording(data, existingId, saved = false) {
        if (this.busy) throw new Error('Wait for the current recording operation to finish, then try again.');
        const item = this.recordings.find(item => item.id === existingId) || this.addRecording(data, saved);
        item.workshopReturn = { boardId: this.workshop.current().id, scrollX: window.scrollX, scrollY: window.scrollY };
        this.pause(); this.selected = item.id; this.position = 0; this.frameOverride = undefined;
        this.workshop.show('replays');
        this.render();
        $('replay-return').focus();
        this.message('Successful solver run. Pause, seek, or use Play from here to try it yourself.');
        this.togglePlayback();
        return item.id;
    },
    returnToWorkshop() {
        const context = this.current()?.workshopReturn;
        if (!context) return;
        this.pause();
        const index = this.workshop.boards.findIndex(board => board.id === context.boardId);
        if (index >= 0 && this.workshop.selected !== index) {
            this.workshop.selected = index;
            this.workshop.changed();
        }
        this.workshop.show('workshop');
        if (index < 0) {
            this.workshop.notify('The source board was removed. Choose a board in Workshop.');
            return;
        }
        requestAnimationFrame(() => {
            if ($('workshop-view').hidden) return;
            window.scrollTo(context.scrollX, context.scrollY);
            const target = $('analysis-recording-actions').hidden ? $('analyze-board') : $('analysis-watch');
            target.focus({ preventScroll: true });
        });
    },
    async openFile(file) {
        $('replay-file').value = '';
        if (!file || this.busy) return;
        this.busy = true; this.render(); this.message('Opening recording…');
        try {
            const data = await decodeReplay(file);
            this.pause();
            const item = this.addRecording(data, true);
            this.selected = item.id; this.position = 0; this.frameOverride = undefined;
            this.message('Recording opened. Your boards and high scores have not changed.');
        } catch (error) { this.message(error.message); }
        finally { this.busy = false; this.render(); }
    },
    async download() {
        const item = this.current();
        if (!item || this.busy) return;
        this.busy = true; this.render(); this.message('Preparing download…');
        try {
            const size = await downloadReplay(item.data);
            item.saved = true;
            this.message(`Download started: ${(size / 1024).toFixed(1)} KB. Keep the file to watch this recording later.`);
        } catch (error) { this.message(error.message); }
        finally { this.busy = false; this.render(); }
    }
};
